import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'

import { useAdminQuery } from '../api'
import type { BacklogReport } from '../types'

/**
 * Poser le backlog de préparation livré avec l'application.
 *
 * Le nombre est annoncé **avant** le geste : quatre cents lignes ne se posent
 * pas à l'aveugle, et « voulez-vous continuer ? » sans chiffre ne se décide pas.
 *
 * La carte disparaît quand il n'y a plus rien à poser. Un bouton qui ne fait
 * plus rien encombre l'écran et finit par être cliqué « pour voir ».
 *
 * Rien de ce qui a été supprimé ne revient : le serveur note ce qui a été
 * *proposé*, pas ce qui subsiste. C'est écrit en toutes lettres sous le bouton,
 * parce que c'est la crainte qu'on a en le regardant.
 */
export function BacklogCard({
  eventId,
  busy,
  error,
  onApply,
}: {
  eventId: string | undefined
  busy: boolean
  error: string | null
  onApply: () => void
}) {
  const { t } = useTranslation()

  const preview = useAdminQuery<BacklogReport>(
    ['admin-backlog-preview', eventId],
    `/admin/events/${eventId}/project/backlog`,
    eventId,
  )

  // Sans le droit de lecture, ou tant que rien n'est chargé : rien à montrer.
  if (preview.isPending || preview.isError) return null
  const report = preview.data
  if (!report || report.created === 0) return null

  return (
    <Card className="mt-6 border-champagne-400/30">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-heading-sm text-content-primary">
            {t('admin.project.backlogTitle')}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-content-secondary">
            {t('admin.project.backlogIntro', { count: report.created })}
          </p>
          {report.skipped > 0 ? (
            <p className="mt-1 text-caption text-content-muted">
              {t('admin.project.backlogSkipped', { count: report.skipped })}
            </p>
          ) : null}
        </div>
        <Button size="sm" disabled={busy} onClick={onApply}>
          {busy ? t('state.loading') : t('admin.project.backlogApply')}
        </Button>
      </div>

      {error ? <p className="mt-3 text-sm text-state-danger">{error}</p> : null}

      <p className="mt-3 text-caption text-content-muted">
        {t('admin.project.backlogSafety')}
      </p>
    </Card>
  )
}
